import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import V2HeroPrompt from '../components/V2HeroPrompt';
import {
  FadeUp, Stagger, StaggerItem, Eyebrow, SectionHead, CtaPrimary, CtaGhost, TextLink,
  AgentAvatar, BigStat, Check, Marquee, RuleLabel, ClosingCta,
} from '../components/V2Shared';
import {
  AGENTS_FRONT, AGENTS_REVENUE, CLINICAL_AI, PLATFORM_PRODUCTS,
  SPRAWL_STACK, MANUAL_TASKS, ARIA_LOOP, GO_LIVE_WEEKS,
} from '../data/site';

const V2Home = () => (
  <V2Layout title="Asha — The AI Operating System for Dental">

    {/* ================= HERO ================= */}
    <section className="v2-blueprint-fade relative overflow-hidden px-6 pt-36 md:pt-40">
      <div className="relative mx-auto max-w-5xl text-center">
        <FadeUp>
          <div className="flex justify-center">
            <Eyebrow label="The first AI-native operating system for dental" />
          </div>
        </FadeUp>
        <FadeUp delay={0.08}>
          <h1 className="v2-display mx-auto mt-7 max-w-4xl text-[56px] font-semibold leading-[0.97] text-[#0a1633] sm:text-[80px] lg:text-[104px]">
            AI Operating System
            <br />
            <span className="text-[#1a46e5]">for Dental</span>
          </h1>
        </FadeUp>
        <FadeUp delay={0.16}>
          <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-[#3f4d6e] md:text-xl">
            One platform replaces the 10+ tools your team juggles every day — then staffs
            it with 20+ AI employees that answer calls, fill the schedule, verify insurance,
            submit claims, and fight denials. End to end, around the clock.
          </p>
        </FadeUp>
        <FadeUp delay={0.24} className="mt-9 flex flex-wrap items-center justify-center gap-4">
          <CtaPrimary />
          <CtaGhost to="/v2/platform">Explore the platform</CtaGhost>
        </FadeUp>
      </div>

      {/* animated prompt → agents graphic */}
      <div className="relative mx-auto mt-16 max-w-7xl md:mt-20">
        <V2HeroPrompt />
      </div>

      {/* counts strip */}
      <div className="relative mx-auto max-w-7xl">
        <FadeUp delay={0.15}>
          <div className="mt-20 grid grid-cols-2 gap-y-10 border-t border-[#dde5f3] pt-12 md:mt-24 md:grid-cols-4">
            <BigStat value="10+" label="software products" />
            <BigStat value="20+" label="AI employees" />
            <BigStat value="40+" label="manual tasks automated" />
            <BigStat value="24/7" label="always on shift" />
          </div>
        </FadeUp>
      </div>
      <div className="mt-16">
        <Marquee items={PLATFORM_PRODUCTS.map((p) => p.name)} />
      </div>
    </section>

    {/* ================= THE PROBLEM ================= */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="01 / the problem" />
        <div className="mt-12 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <SectionHead
            eyebrow="Software sprawl"
            title={<>Ten tools. Ten logins. None of them talk.</>}
            body="A typical practice runs its day across a PMS, a phone system, a verification portal, a clearinghouse, a marketing tool, an imaging server, and more — each one a silo, each one a subscription, each one a place where work falls through the cracks. You can never automate a workflow that's scattered across ten vendors."
          />
          <FadeUp delay={0.1}>
            <div className="overflow-hidden rounded-2xl border border-[#dde5f3]">
              <div className="v2-mono flex items-center justify-between bg-[#f6f9ff] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7c89a8]">
                <span>the stack you're juggling today</span>
                <span>10 vendors</span>
              </div>
              {SPRAWL_STACK.map((s) => (
                <div key={s.name} className="v2-row flex items-baseline justify-between gap-4 px-5 py-3">
                  <span className="text-[14.5px] font-semibold text-[#0a1633]">{s.name}</span>
                  <span className="v2-mono text-right text-[11.5px] text-[#a0aac4] line-through decoration-[#d8614f]/60">
                    {s.vendors}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between gap-4 border-t-2 border-[#1a46e5] bg-[#eef3ff] px-5 py-4">
                <span className="v2-display text-[18px] font-semibold text-[#0a1633]">
                  Asha — all of it, one platform
                </span>
                <TextLink to="/v2/why-asha">The full case</TextLink>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* ================= PLATFORM (dark panel) ================= */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHead
              dark
              eyebrow="02 / the platform"
              title={<>Every system your practice needs. Already connected.</>}
              body="Practice management, phones, claims, imaging, payments, marketing, analytics — built as one product on one data model. Not integrated. Native."
            />
            <FadeUp delay={0.15}>
              <CtaGhost to="/v2/platform" onBlue>Tour all 10 products</CtaGhost>
            </FadeUp>
          </div>

          <Stagger className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/12 bg-white/12 sm:grid-cols-2 lg:grid-cols-5">
            {PLATFORM_PRODUCTS.map((p) => (
              <StaggerItem key={p.key} className="h-full">
                <Link
                  to="/v2/platform"
                  className="group flex h-full flex-col gap-3 bg-[#0d2391]/80 p-6 transition-colors duration-200 hover:bg-[#1236bd]"
                >
                  <span className="v2-mono flex items-center justify-between text-[11px] font-medium text-[#9db4ff]">
                    {p.index}
                    {p.soon && <span className="rounded-full border border-[#9db4ff]/40 px-2 py-0.5 text-[9.5px] uppercase tracking-wider">soon</span>}
                  </span>
                  <span className="v2-display text-[19px] font-semibold leading-tight text-white">{p.name}</span>
                  <span className="text-[13px] leading-relaxed text-white/60">{p.desc}</span>
                  <span className="mt-auto flex items-center gap-1 pt-2 text-[12.5px] font-bold text-[#9db4ff] opacity-0 transition-opacity group-hover:opacity-100">
                    Explore <ArrowRight className="h-3 w-3" />
                  </span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>

    {/* ================= AI WORKFORCE ================= */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="03 / the ai workforce" />
        <div className="mt-12 flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            eyebrow="20+ AI employees"
            title={<>Not another tool for your team.<br />A team for your team.</>}
            body="Every agent has a name, a job, and real authority to finish it — booking the call, filling the chair, submitting the claim, drafting the appeal. They share one memory, so nothing gets lost in a handoff."
          />
          <FadeUp delay={0.15}>
            <CtaGhost to="/v2/ai-team">Meet all of them</CtaGhost>
          </FadeUp>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2">
          {[
            { label: 'Front office', agents: AGENTS_FRONT, to: '/v2/front-office', blurb: 'Calls, scheduling, recall, intake, reputation.' },
            { label: 'Revenue cycle', agents: AGENTS_REVENUE, to: '/v2/revenue-cycle', blurb: 'Verification, claims, posting, denials, A/R.' },
          ].map((group) => (
            <FadeUp key={group.label}>
              <div className="overflow-hidden rounded-2xl border border-[#dde5f3]">
                <div className="flex items-center justify-between bg-[#f6f9ff] px-6 py-4">
                  <div>
                    <div className="v2-display text-[20px] font-semibold text-[#0a1633]">{group.label}</div>
                    <div className="text-[13px] text-[#7c89a8]">{group.blurb}</div>
                  </div>
                  <TextLink to={group.to}>Deep dive</TextLink>
                </div>
                {group.agents.map((a) => (
                  <Link key={a.name} to={group.to} className="v2-row group flex items-center gap-4 px-6 py-3.5">
                    <span className="v2-mono w-12 shrink-0 text-[11px] font-medium text-[#a8bcf0]">{a.tag}</span>
                    <AgentAvatar name={a.name} img={a.img} size="h-10 w-10" />
                    <div className="min-w-0">
                      <div className="text-[15px] font-bold text-[#0a1633] group-hover:text-[#1a46e5]">{a.name}</div>
                      <div className="truncate text-[13px] text-[#7c89a8]">{a.role} — {a.desc}</div>
                    </div>
                    <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-[#c4d2ec] transition-all group-hover:translate-x-1 group-hover:text-[#1a46e5]" />
                  </Link>
                ))}
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* ================= ARIA ================= */}
    <section className="px-6 pb-24 md:pb-32">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="04 / the engine" />
        <div className="mt-12 grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              eyebrow="ARIA — Asha Reasoning & Intelligence Architecture"
              title={<>Most dental AI assists. ARIA finishes the job.</>}
              body="Underneath every agent is one autonomy engine. ARIA reasons about the goal, dispatches the right specialist, reads what came back, and loops until the workflow is actually done — no hand-back to a human mid-task."
            />
            <FadeUp delay={0.1} className="mt-8">
              <TextLink to="/v2/aria">See how the engine works</TextLink>
            </FadeUp>
          </div>
          <Stagger className="space-y-3">
            {ARIA_LOOP.map((s, i) => (
              <StaggerItem key={s.step}>
                <div className="v2-card flex gap-5 rounded-2xl p-6">
                  <span className="v2-display text-[28px] font-semibold leading-none text-[#c4d2ec]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <div className="v2-display text-[19px] font-semibold text-[#0a1633]">{s.step}</div>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-[#3f4d6e]">{s.desc}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>

    {/* ================= 40+ TASKS ================= */}
    <section className="pb-24 md:pb-32">
      <div className="mx-auto mb-10 max-w-7xl px-6">
        <SectionHead
          center
          eyebrow="05 / the busywork"
          title={<>40+ manual tasks, off your team's plate.</>}
          body="Every line below is something a human at your practice does today — and something the AI workforce runs on its own inside Asha."
        />
      </div>
      <Marquee items={MANUAL_TASKS.slice(0, 20)} />
      <Marquee items={MANUAL_TASKS.slice(20)} fast />
    </section>

    {/* ================= CLINICAL AI ================= */}
    <section className="v2-canvas px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            eyebrow="06 / clinical ai"
            title={<>AI in the operatory, not just the office.</>}
            body="Notes that write themselves, treatment plans patients understand, perio charting by voice, and a copilot that knows the chart."
          />
          <FadeUp delay={0.15}>
            <CtaGhost to="/v2/clinical-ai">Explore Clinical AI</CtaGhost>
          </FadeUp>
        </div>
        <Stagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {CLINICAL_AI.map((c) => (
            <StaggerItem key={c.name} className="h-full">
              <Link to="/v2/clinical-ai" className="v2-card group flex h-full flex-col rounded-2xl p-7">
                <span className="v2-mono text-[11px] font-medium text-[#a8bcf0]">{c.tag}</span>
                <span className="v2-display mt-3 text-[21px] font-semibold leading-tight text-[#0a1633] group-hover:text-[#1a46e5]">
                  {c.name}
                </span>
                <p className="mt-3 text-[14px] leading-relaxed text-[#3f4d6e]">{c.desc}</p>
                <ArrowRight className="mt-auto h-4 w-4 translate-y-2 text-[#1a46e5] opacity-0 transition-all group-hover:translate-y-0 group-hover:opacity-100" />
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    {/* ================= SWITCH TEASER ================= */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <RuleLabel label="07 / switching" />
        <div className="mt-12 flex flex-wrap items-end justify-between gap-8">
          <SectionHead
            eyebrow="White-glove onboarding"
            title={<>Live in four weeks. Without the chaos.</>}
            body="A dedicated implementation team migrates your data, configures your workflows, and phases the AI in — front office first, back office when you're ready."
          />
          <FadeUp delay={0.15}>
            <CtaGhost to="/v2/switch">How switching works</CtaGhost>
          </FadeUp>
        </div>
        <Stagger className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {GO_LIVE_WEEKS.map((w) => (
            <StaggerItem key={w.week} className="h-full">
              <div className="v2-card h-full rounded-2xl p-7">
                <div className="v2-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[#1a46e5]">{w.week}</div>
                <div className="v2-display mt-3 text-[20px] font-semibold leading-tight text-[#0a1633]">{w.title}</div>
                <ul className="mt-4 space-y-2.5">
                  {w.items.slice(0, 3).map((item) => (
                    <Check key={item}>{item}</Check>
                  ))}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <ClosingCta />
  </V2Layout>
);

export default V2Home;
